/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '@/components/Icon';
import { formatReleaseDate, RELEASE_NOTES, type ReleaseTag } from '@/features/releaseNotes/data';
import { Modal } from './Modal';

const TAG_META: Record<ReleaseTag, { labelKey: string; icon: IconName }> = {
  feature: { labelKey: 'releaseNotes.tags.feature', icon: 'spark' },
  improvement: { labelKey: 'releaseNotes.tags.improvement', icon: 'arrow' },
  fix: { labelKey: 'releaseNotes.tags.fix', icon: 'check' },
};

/** What's new dialog (versioned release notes, newest first). */
export function ReleaseNotesModal({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();

  return (
    <Modal onClose={onClose} labelledBy="rn-title">
      <div className="modal rn-modal">
        <div className="modal-head">
          <Icon
            name="spark"
            size={22}
            style={{ color: 'var(--color-primary-text)', flex: 'none' }}
          />
          <div className="mh-titles">
            <h2 id="rn-title">{t('releaseNotes.title')}</h2>
            <div className="mh-sub">{t('releaseNotes.subtitle')}</div>
          </div>
          <div className="sp" style={{ flex: 1 }} />
          <button className="iclose" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="ov-body rn-body">
          {RELEASE_NOTES.map((note, i) => (
            <section key={note.version} className="rn-rel">
              <div className="rn-rel-head">
                <span className="rn-ver">v{note.version}</span>
                {i === 0 && <span className="rn-latest">{t('releaseNotes.latest')}</span>}
                <span className="rn-date">{formatReleaseDate(note.date, i18n.language)}</span>
              </div>
              <div className="rn-title">{t(note.title)}</div>
              {note.intro && <p className="rn-intro">{t(note.intro)}</p>}
              <ul className="rn-list">
                {note.highlights.map((h) => (
                  <li key={h.text} className="rn-item">
                    <span className={`rn-tag rn-tag-${h.tag}`}>
                      <Icon name={TAG_META[h.tag].icon} size={13} />
                      {t(TAG_META[h.tag].labelKey)}
                    </span>
                    <span className="rn-text">{t(h.text)}</span>
                  </li>
                ))}
              </ul>
              {note.outro && <p className="rn-outro">{t(note.outro)}</p>}
            </section>
          ))}
        </div>

        <div className="ov-foot set-foot">
          <button className="btn btn-pri" onClick={onClose}>
            {t('releaseNotes.gotIt')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
